import React, {
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
  forwardRef,
  useEffect,
} from "react";
import * as yup from "yup";

import { Box } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import Button, { Color } from "Components/Form/Button";
import { AppDrawer, AppDrawerProps } from "Components/Generic/AppDrawer";
import { useTranslation } from "react-i18next";

import { EmailSubscriptionFormSection, SelectedFiltersSection } from "./sections";
import { Cronjob } from "Models/cronjob";
import SmsNotificationSuccess from "Components/Generic/Icons/SmsNotificationSuccess";
import { CronjobFrequency } from "Util/enums";

export interface ManageEmailSubscriptionEditorDrawerHandles {
  open: (emailSubscription: Cronjob) => void;
  close: () => void;
}

interface ManageEmailSubscriptionsProps {
  submitCronJob: (cronJob?: Cronjob, changes?: string) => void;
  isLoading?: boolean;
}

export const ManageEmailSubscriptionEditorDrawer = forwardRef<
  ManageEmailSubscriptionEditorDrawerHandles,
  ManageEmailSubscriptionsProps
>((props, ref) => {
  const { t: translate } = useTranslation();
  const { submitCronJob, isLoading } = props;

  const [isOpenFleetShowDrawer, setIsOpenFleetShowDrawer] = useState<boolean>(false);
  const [changes, setChanges] = useState<string[]>([]);
  const [emailSubscription, setEmailSubscription] = useState<Cronjob>();
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

  const title = useMemo(() => {
    if (!emailSubscription?.id) return `${translate("sendReportByEmail")}`;

    return `${translate("sendReportByEmail")} - #${emailSubscription?.id}`;
  }, [emailSubscription]);

  const open = useCallback((newEmailSubscription: Cronjob) => {
    setEmailSubscription(newEmailSubscription);
    setIsOpenFleetShowDrawer(true);
  }, []);

  const close = useCallback(() => {
    setHasBeenSubmitted(false);
    setErrors([]);
    setEmailSubscription(undefined);
    setIsOpenFleetShowDrawer(false);
  }, []);

  const schemaValidation = yup.object().shape({
    cronjobContacts: yup
      .array()
      .min(1)
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    frequency: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    day: yup.string().when("frequency", {
      is: (value?: string) =>
        !value?.trim().length || value?.toLowerCase() !== CronjobFrequency.ONCE.toLowerCase(),
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
    time: yup.string().when("frequency", {
      is: (value?: string) =>
        !value?.trim().length || value?.toLowerCase() !== CronjobFrequency.ONCE.toLowerCase(),
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
  });

  const validateEmailSubscription = () => {
    try {
      schemaValidation.validateSync(emailSubscription, { abortEarly: false });
      setErrors([]);
      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        console.log(error.inner);
        setErrors(error.inner);
      }

      return false;
    }
  };

  const handleSubmitReportSubscription = () => {
    setHasBeenSubmitted(true);

    if (!emailSubscription) return;

    const isEmailSubscriptionValid = validateEmailSubscription();

    if (isEmailSubscriptionValid) {
      submitCronJob(emailSubscription, changes.join(", "));
    }
  };

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const sections: AppDrawerProps["sectionItems"] = [
    {
      id: "formFields",
      content: emailSubscription && (
        <EmailSubscriptionFormSection
          errors={errors}
          cronJob={emailSubscription}
          updateCronJob={setEmailSubscription}
          setChanges={(change: string) => setChanges((prev) => [...(prev || []), change])}
        />
      ),
    },
    {
      id: "selectedFilters",
      title: translate("selectedFilters"),
      content: emailSubscription && (
        <SelectedFiltersSection
          cronJob={emailSubscription}
          updateCronJob={setEmailSubscription}
          setChanges={(change: string) => setChanges((prev) => [...(prev || []), change])}
        />
      ),
    },
  ];

  useEffect(() => {
    if (hasBeenSubmitted && emailSubscription) {
      validateEmailSubscription();
    }
  }, [hasBeenSubmitted, emailSubscription]);

  return (
    <AppDrawer
      DrawerProps={{
        open: isOpenFleetShowDrawer,
        onClose: close,
        sx: { color: colors.gray600 },
      }}
      title={title}
      footer={
        <Box display="flex" justifyContent="space-between" width="100%">
          <Button customColor={Color.BRAND_TEXT} onClick={close}>
            {translate("close")}
          </Button>

          <Button
            startIcon={<SmsNotificationSuccess height={16} width={16} color="#fff" />}
            customColor={Color.BRAND}
            disabled={isLoading}
            onClick={() => handleSubmitReportSubscription()}
            loading={isLoading}
          >
            {emailSubscription?.id ? translate("update") : translate("schedule")}
          </Button>
        </Box>
      }
      hideSideMenu
      sectionItems={sections}
    />
  );
});
